// =====================================================
// SHIFT MANAGEMENT TYPES
// =====================================================

export interface Shift {
  id: string;
  name: string;
  code: string;
  description: string | null;
  start_time: string; // HH:MM:SS format
  end_time: string; // HH:MM:SS format
  crosses_midnight: boolean;
  color: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BreakType {
  id: string;
  name: string;
  code: string;
  default_duration_minutes: number;
  is_paid: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ShiftBreak {
  id: string;
  shift_id: string;
  break_type_id: string;
  start_offset_minutes: number;
  duration_minutes: number;
  is_mandatory: boolean;
  created_at: string;
  // Expanded relations
  break_type?: BreakType;
}

export interface WorkerShiftAssignment {
  id: string;
  worker_id: string;
  shift_id: string;
  effective_date: string; // YYYY-MM-DD
  end_date: string | null;
  assigned_by: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Expanded relations
  shift?: Shift;
  worker?: {
    id: string;
    full_name: string;
    employee_id: string | null;
    area: string | null;
  };
  assigner?: {
    id: string;
    full_name: string;
  };
}

// Extended Shift with breaks for UI
export interface ShiftWithBreaks extends Shift {
  breaks: ShiftBreak[];
}

// Shift summary from view
export interface ShiftSummary extends Shift {
  break_count: number;
  total_break_minutes: number;
}

// Current shift assignment from view
export interface CurrentShiftAssignment {
  worker_id: string;
  full_name: string;
  employee_id: string | null;
  area: string | null;
  worker_active: boolean;
  shift_id: string | null;
  shift_name: string | null;
  shift_code: string | null;
  start_time: string | null;
  end_time: string | null;
  shift_color: string | null;
  effective_date: string | null;
  assignment_notes: string | null;
}

// Form data types for creating/updating
export interface ShiftFormData {
  name: string;
  code: string;
  description?: string | null;
  start_time: string;
  end_time: string;
  crosses_midnight: boolean;
  color: string;
  is_active: boolean;
}

export interface BreakTypeFormData {
  name: string;
  code: string;
  default_duration_minutes: number;
  is_paid: boolean;
  is_active: boolean;
}

export interface ShiftBreakFormData {
  shift_id: string;
  break_type_id: string;
  start_offset_minutes: number;
  duration_minutes: number;
  is_mandatory: boolean;
}

export interface WorkerShiftAssignmentFormData {
  worker_id: string;
  shift_id: string;
  effective_date: string;
  end_date?: string | null;
  notes?: string | null;
}

// Utility type for time display
export interface TimeRange {
  start: string;
  end: string;
  crossesMidnight: boolean;
}

// Helper function types
export type ShiftCode = 'M' | 'T' | 'N' | string;

// Color presets for shifts
export const SHIFT_COLORS = [
  { name: 'Verde', value: '#22c55e' },
  { name: 'Naranja', value: '#f59e0b' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Azul', value: '#3b82f6' },
  { name: 'Rojo', value: '#ef4444' },
  { name: 'Rosa', value: '#ec4899' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Gris', value: '#6b7280' },
] as const;

// Day schedule for each day of the week
export interface ShiftDaySchedule {
  id?: string;
  shift_id?: string;
  day_of_week: number; // 0=Sunday, 1=Monday, ..., 6=Saturday
  start_time: string;
  end_time: string;
  is_working_day: boolean;
  created_at?: string;
  updated_at?: string;
}

// Extended Shift with day schedules
export interface ShiftWithSchedules extends Shift {
  breaks: ShiftBreak[];
  day_schedules: ShiftDaySchedule[];
}

// Day names in Spanish
export const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'] as const;
export const DAY_NAMES_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'] as const;
